/**
 * Where "learn it" points, for a moment whose remedy is `knowledge`.
 *
 * A knowledge remedy says the student has to understand something before any
 * of it is worth drilling — which is only useful if the card can name what to
 * revise. These are ids from the `concepts` registry, so they are picked from
 * the registry rather than typed: an id that resolves to nothing is a card
 * with an empty destination, which is the failure the axis exists to prevent.
 *
 * Scoped to the moment's subjects when it has them; a generic moment sees the
 * whole registry, filtered by the search box.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { Concept } from '../../types';

interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
  /** Subject ids of the moment being edited; empty ⇒ every subject. */
  subjects: string[];
}

const SUBJECTS = ['maths', 'physics', 'chemistry', 'biology'];

export function ConceptPicker({ value, onChange, subjects }: Props) {
  const [term, setTerm] = useState('');
  const scope = subjects.length > 0 ? subjects : SUBJECTS;

  const { data: concepts, isLoading } = useQuery({
    queryKey: ['lm-concepts', scope.join(',')],
    queryFn: async () => {
      const pages = await Promise.all(scope.map(s => adminApi.listConcepts({ subject: s })));
      return pages.flatMap(p => p.concepts);
    },
  });

  const byId = useMemo(
    () => new Map((concepts ?? []).map(c => [c.id, c])),
    [concepts],
  );

  // Only ever show a shortlist: the registry runs to hundreds per subject, and
  // an unfiltered list is not a choice anyone makes.
  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [] as Concept[];
    return (concepts ?? [])
      .filter(c => c.name.toLowerCase().includes(q) || c.id.includes(q))
      .slice(0, 12);
  }, [concepts, term]);

  return (
    <div className="lm-exemplars">
      <div className="lm-module-chips">
        {value.length === 0
          ? <span className="lm-module-empty">
              None chosen — a knowledge moment with no concept has nowhere to send the student.
            </span>
          : value.map(id => (
            <span key={id} className={`lm-module-chip${byId.size > 0 && !byId.has(id) ? ' is-unknown' : ''}`}>
              {byId.get(id)?.name ?? id}
              <button type="button" onClick={() => onChange(value.filter(x => x !== id))} aria-label="Remove concept">✕</button>
            </span>
          ))}
      </div>

      <div className="lm-exemplar-search">
        <input
          type="text"
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder={isLoading ? 'Loading concepts…' : 'Search concepts by name…'}
        />
      </div>

      {term.trim().length >= 2 && matches.length === 0 && !isLoading && (
        <p className="lm-hint">No concept matched in {scope.join(', ')}.</p>
      )}
      {matches.length > 0 && (
        <ul className="lm-exemplar-results">
          {matches.map(c => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => { if (!value.includes(c.id)) onChange([...value, c.id]); }}
                disabled={value.includes(c.id)}
              >
                {value.includes(c.id) ? '✓ ' : '+ '}{c.name} <span className="lm-hint">{c.subject}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
